import { Component, OnInit, Input, HostListener, ElementRef } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { UploadCsvComponent } from '../../pages/upload-csv/upload-csv.component' // adjust path if needed
import { AuthManager } from 'src/app/shared/auth/AuthManager'
import { ApplicationArea } from 'src/app/shared/services/ppv.api'
import { Subscription } from 'rxjs/internal/Subscription'
import { Subject, of } from 'rxjs'
import { debounceTime, distinctUntilChanged, switchMap, catchError, finalize } from 'rxjs/operators'
import { LeaderService } from '../../services/leader.service'
import { Router } from '@angular/router'

/**
 * How long the header bar waits for the user to stop typing before reacting.
 * Tuned for "search-as-you-type" — long enough to skip mid-word keystrokes,
 * short enough that a finished query still feels instant.
 */
const HEADER_SEARCH_DEBOUNCE_MS = 500
/**
 * Minimum characters before we fire any search-related action (API call OR
 * URL navigation on the search page). One-character queries match almost
 * everything and produce noise — skipping them eliminates a large class of
 * unnecessary API calls without hurting real-world UX.
 */
const MIN_SEARCH_CHARS = 2

@Component({
   selector: 'header-row',
   templateUrl: './header-row.component.html',
   styleUrl: './header-row.component.scss',
   standalone: false,
})
export class HeaderRowComponent implements OnInit {
   @Input() userName: string = 'User Name'
   @Input() userRole: string = 'ROLE'
   @Input() avatarUrl?: string

   userNameData: any
   userRoleData: string = 'ROLE'
   canViewHospitalManagerDashboard!: boolean
   canViewRM: boolean = false
   canViewOpsDirector: boolean = false
   canViewOpsVP: boolean = false
   isLoading: boolean = true
   showTimeoutModal = false
   private notificationIntervalId: ReturnType<typeof setInterval> | null = null
   private notificationEventSubscription: Subscription | null = null
   navItems: NavItem[] = []
   private readonly roleNameMap: Record<string, string> = {
      LTKRM: 'RM',
   }

   // Mobile navigation state
   showMobileMenu: boolean = false
   showMobileSearch: boolean = false

   toggleMobileMenu() {
      this.showMobileMenu = !this.showMobileMenu
      if (this.showMobileMenu) this.showMobileSearch = false
   }

   toggleMobileSearch() {
      this.showMobileSearch = !this.showMobileSearch
      if (this.showMobileSearch) this.showMobileMenu = false
   }

   // Global Search variables
   searchQuery: string = ''
   showSearchDropdown: boolean = false
   isSearchLoading: boolean = false
   isSearchError: boolean = false
   searchResults: any = { goals: [], tasks: [], meetings: [], hospitals: [] }
   // Full last `res.data` (query, totalResults, results). Kept separate from
   // `searchResults` (which is only the inner results object the dropdown
   // template binds to) so the "View All Results" action can hand the
   // pre-fetched payload to the Search Results page and skip an API call.
   private lastSearchData: any = null
   private searchSubject = new Subject<string>()

   constructor(
      private dialog: MatDialog,
      private authMgr: AuthManager,
      private leaderService: LeaderService,
      private router: Router,
      private eRef: ElementRef
   ) {}

   ngOnInit(): void {
      this.getRoleBasedRoute()
      this.getNotificationIcon()
      this.startNotificationPolling()
      this.nofificationEvent()
      this.setupSearch()
      this.prefillSearchFromUrl()
   }

   get isHMRole(): boolean {
      return this.userRoleData === 'HM'
   }

   /**
    * If the page we just loaded is the dedicated Search Results page
    * (e.g. browser-back, refresh, or a direct link), copy the `q` query
    * param into the header search bar so the single shared input keeps the
    * text the user originally typed.
    */
   private prefillSearchFromUrl(): void {
      if (!this.isOnSearchPage()) return
      try {
         const url = this.router.url || ''
         const queryString = url.split('?')[1] || ''
         const params = new URLSearchParams(queryString)
         const q = params.get('q') || ''
         if (q && !this.searchQuery) {
            this.searchQuery = q
         }
      } catch {
         /* ignore parse errors */
      }
   }

   getRoleBasedRoute() {
      this.authMgr.getCurrentUser().subscribe((user) => {
         this.userNameData = user?.displayName || 'User Name'
         const appRole: any = user?.roles?.find((r: any) => r?.application === 52 && r?.roleName !== 'LeadershipToolKit')
         const roleName = this.normalizeRoleName(appRole?.roleName)
         this.userRoleData = roleName || this.userRole || 'ROLE'

         const userHospitalManager = this.authMgr.canAccess(user, {
            application: ApplicationArea.HospitalManager,
            roleName: ['HospitalManager'],
         })
         const userRM = this.authMgr.canAccess(user, {
            application: ApplicationArea.LTK,
            roleName: ['RM'],
         })
         const userOpsDirector = this.authMgr.canAccess(user, {
            application: ApplicationArea.OpsDirector,
            roleName: ['OpsDirector'],
         })
         const userOpsVP = this.authMgr.canAccess(user, {
            application: ApplicationArea.OpsVP,
            roleName: ['OpsVP'],
         })
         this.canViewHospitalManagerDashboard = userHospitalManager
         this.canViewRM = userRM
         this.canViewOpsDirector = userOpsDirector
         this.canViewOpsVP = userOpsVP
         this.buildDynamicNavItems(userHospitalManager, userRM, userOpsDirector, userOpsVP)
      })
   }

   private buildDynamicNavItems(isHospitalManager: boolean, isRM: boolean, isOpsDirector: boolean, isOpsVP: boolean): void {
      this.navItems = []
      if (this.userRoleData == 'RM' || this.userRoleData == 'AM' || this.userRoleData == 'AreaManager') {
         this.navItems = [
            { label: 'Dashboard', icon: 'dashboard', route: '/leader-toolkit-ops-dashboard' },
            { label: 'Hospital Dashboard', icon: 'dashboard', route: '/leader-toolkit-hospital-dashboard' },
            { label: 'Goals', icon: 'bar_chart', route: '/leader-toolkit-goals' },
            { label: 'Tasks', icon: 'assignment', route: '/leader-toolkit-tasks' },
            { label: 'Scheduling', icon: 'calendar_today', route: '/leader-toolkit-scheduling' },
         ]
      } else if (this.userRoleData == 'ADM') {
         this.navItems = [
            { label: 'Dashboard', icon: 'manage_accounts', route: '/leader-toolkit-ops-dashboard' },
            { label: 'Hospital Dashboard', icon: 'dashboard', route: '/leader-toolkit-hospital-dashboard' },
            { label: 'Goals', icon: 'bar_chart', route: '/leader-toolkit-goals' },
            { label: 'Tasks', icon: 'assignment', route: '/leader-toolkit-tasks' },
            { label: 'Scheduling', icon: 'calendar_today', route: '/leader-toolkit-scheduling' },
            { label: 'User Management', icon: 'supervisor_account', route: '/leader-toolkit-user-management' },
            { label: 'Upload Goals', icon: 'cloud_upload', route: '/leader-upload-goals' },
         ]
      } else if (this.userRoleData == 'VP') {
         this.navItems = [
            { label: 'Dashboard', icon: 'manage_accounts', route: '/leader-toolkit-ops-dashboard' },
            { label: 'Hospital Dashboard', icon: 'dashboard', route: '/leader-toolkit-hospital-dashboard' },
            { label: 'Goals', icon: 'bar_chart', route: '/leader-toolkit-goals' },
            { label: 'Tasks', icon: 'assignment', route: '/leader-toolkit-tasks' },
            { label: 'Scheduling', icon: 'calendar_today', route: '/leader-toolkit-scheduling' },
            { label: 'User Management', icon: 'supervisor_account', route: '/leader-toolkit-user-management' },
            { label: 'Upload Goals', icon: 'cloud_upload', route: '/leader-upload-goals' },
         ]
      } else if (this.userRoleData == 'DO') {
         this.navItems = [
            { label: 'Dashboard', icon: 'manage_accounts', route: '/leader-toolkit-ops-dashboard' },
            { label: 'Hospital Dashboard', icon: 'dashboard', route: '/leader-toolkit-hospital-dashboard' },
            { label: 'Goals', icon: 'bar_chart', route: '/leader-toolkit-goals' },
            { label: 'Tasks', icon: 'assignment', route: '/leader-toolkit-tasks' },
            { label: 'Scheduling', icon: 'calendar_today', route: '/leader-toolkit-scheduling' },
            { label: 'Upload Goals', icon: 'cloud_upload', route: '/leader-upload-goals' },
         ]
      } else if (this.userRoleData == 'HM') {
         this.navItems = [{ label: 'Hospital Dashboard', icon: 'dashboard', route: '/leader-toolkit-hospital-dashboard' }]
      }
   }

   private normalizeRoleName(roleName?: string): string {
      if (!roleName) {
         return ''
      }
      const normalized = roleName.trim()
      return this.roleNameMap[normalized] || normalized
   }

   ngOnDestroy(): void {
      if (this.notificationIntervalId) {
         clearInterval(this.notificationIntervalId)
         this.notificationIntervalId = null
      }

      if (this.notificationEventSubscription) {
         this.notificationEventSubscription.unsubscribe()
         this.notificationEventSubscription = null
      }
   }

   //-------------------------- Notification panel logic --------------------------

   showNotifications = false
   notifications: any = []
   notificationShow() {
      this.showNotifications = !this.showNotifications
   }

   getNotificationIcon() {
      this.leaderService.getNotifications().subscribe((data: any) => {
         this.notifications = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []
      })
   }

   private startNotificationPolling() {
      if (this.notificationIntervalId) {
         clearInterval(this.notificationIntervalId)
      }
      this.notificationIntervalId = setInterval(() => {
         this.getNotificationIcon()
      }, 60 * 1000)
   }

   closeNotification(id: number) {
      this.leaderService.updateNotificationAsRead(id).subscribe(() => {
         this.getNotificationIcon()
      })
   }
   nofificationEvent() {
      if (this.notificationEventSubscription) {
         this.notificationEventSubscription.unsubscribe()
      }

      this.notificationEventSubscription = this.leaderService.notificationEvent.subscribe((data) => {
         if (data === 'notification') {
            this.getNotificationIcon()
         }
      })
   }

   taskNavigation(notification: any) {
      this.notificationShow()
      if (notification.type == 'Task') {
         this.router.navigate(['/leader-toolkit-tasks'])
      } else if (notification.type == 'Goal') {
         this.router.navigate(['/leader-toolkit-goals'])
      } else if (notification.type == 'OPSDashboard') {
         this.router.navigate(['/leader-toolkit-ops-dashboard'])
      } else if (notification.type == 'Meeting') {
         this.router.navigate(['/leader-toolkit-scheduling'])
      } else if (notification.type == 'UploadGoal') {
         this.router.navigate(['/leader-upload-goals'])
      } else if (notification.type == 'HospitalEditor') {
         this.router.navigate(['/hospital-editor'])
      } else {
         this.router.navigate(['/'])
      }
   }

   //-------------------------- Global Search logic --------------------------

   setupSearch() {
      // Debounce strategy:
      //   - debounceTime(HEADER_SEARCH_DEBOUNCE_MS) waits for typing to settle
      //     before any side effect fires. This is the primary throttle.
      //   - distinctUntilChanged blocks no-op emissions (e.g. value reset to
      //     itself programmatically).
      //   - MIN_SEARCH_CHARS guard prevents 1-character "noise" queries from
      //     hitting the API at all.
      // Net effect: at most one downstream action per typing burst, and zero
      // actions for trivially short queries.
      this.searchSubject
         .pipe(
            debounceTime(HEADER_SEARCH_DEBOUNCE_MS),
            distinctUntilChanged(),
            switchMap((query) => {
               const trimmed = (query || '').trim()

               // Empty query — clear local state and (on search page) wipe URL.
               if (!trimmed) {
                  this.searchResults = { goals: [], tasks: [], meetings: [], hospitals: [] }
                  this.lastSearchData = null
                  this.showSearchDropdown = false
                  if (this.isOnSearchPage()) {
                     this.router.navigate(['/leader-toolkit-search'], { queryParams: {} })
                  }
                  return of(null)
               }

               // Min-char guard — skip API and URL nav for sub-threshold queries.
               // Also clear any previously-fetched results so the dropdown
               // doesn't briefly flash stale data if the user re-extends the
               // query past the threshold.
               if (trimmed.length < MIN_SEARCH_CHARS) {
                  this.searchResults = { goals: [], tasks: [], meetings: [], hospitals: [] }
                  this.lastSearchData = null
                  this.showSearchDropdown = false
                  this.isSearchLoading = false
                  return of(null)
               }

               // When already on the dedicated search page, the header bar acts
               // as a thin controller — it never opens the dropdown and never
               // hits the API itself. It just updates the URL; the search page
               // owns the fetch through its own pipeline.
               if (this.isOnSearchPage()) {
                  this.showSearchDropdown = false
                  this.router.navigate(['/leader-toolkit-search'], {
                     queryParams: { q: trimmed, page: 1, type: 'all' },
                  })
                  return of(null)
               }

               // Default (other pages): fetch + show dropdown.
               this.isSearchLoading = true
               this.isSearchError = false
               this.showSearchDropdown = true
               return this.leaderService.globalSearch(trimmed).pipe(
                  catchError((err) => {
                     this.isSearchError = true
                     return of(null)
                  }),
                  finalize(() => (this.isSearchLoading = false))
               )
            })
         )
         .subscribe((res: any) => {
            if (res?.data) {
               this.lastSearchData = res.data
               if (res.data.results) {
                  this.searchResults = res.data.results
               }
            }
         })
   }

   /**
    * True when the user is currently on the dedicated Search Results page.
    * Used by the header bar to switch behaviour: no dropdown, no separate
    * API call — just keep the URL in sync so the page can react.
    */
   private isOnSearchPage(): boolean {
      return (this.router.url || '').split('?')[0] === '/leader-toolkit-search'
   }

   onSearchInput(event: any) {
      const query = event.target.value
      this.searchQuery = query

      if (this.isOnSearchPage()) {
         this.showSearchDropdown = false
      } else if (query.trim().length >= MIN_SEARCH_CHARS) {
         this.showSearchDropdown = true
         this.isSearchLoading = true
      } else {
         this.showSearchDropdown = false
         this.isSearchLoading = false
      }

      this.searchSubject.next(query)
   }

   onSearchFocus() {
      // Never show the dropdown on the search results page.
      if (this.isOnSearchPage()) return
      if (this.searchQuery.trim()) {
         this.showSearchDropdown = true
      }
   }

   clearSearch() {
      this.searchQuery = ''
      this.showSearchDropdown = false
      this.searchResults = { goals: [], tasks: [], meetings: [], hospitals: [] }
      this.lastSearchData = null
      // If we're on the search page, also wipe the URL so the page resets.
      if (this.isOnSearchPage()) {
         this.router.navigate(['/leader-toolkit-search'], { queryParams: {} })
      }
   }

   @HostListener('document:click', ['$event'])
   clickout(event: any) {
      if (!this.eRef.nativeElement.contains(event.target)) {
         this.showSearchDropdown = false
         this.showMobileMenu = false
         this.showMobileSearch = false
      }
   }

   hasSearchResults(): boolean {
      return (
         this.searchResults?.goals?.length > 0 ||
         this.searchResults?.tasks?.length > 0 ||
         this.searchResults?.meetings?.length > 0 ||
         this.searchResults?.hospitals?.length > 0
      )
   }

   navigateToResult(item: any) {
      this.showSearchDropdown = false
      const module = (item.module || '').toLowerCase()
      const urlId = item.url ? +item.url.split('/').pop() : null
      const id = urlId && urlId !== item.id ? urlId : item.id

      if (module === 'hospital') {
         this.router.navigate(['/leader-tookit-hospitals', id, 'ops-dashboard'])
         return
      }

      const routeMap: Record<string, string> = {
         task: '/leader-toolkit-tasks',
         goal: '/leader-toolkit-goals',
         meeting: '/leader-toolkit-scheduling',
      }
      const destination = routeMap[module]
      if (!destination) return

      const currentRoute = this.router.url.split('?')[0]
      if (currentRoute === destination) {
         this.leaderService.triggerSearchHighlight(module, id)
      } else {
         this.router.navigate([destination], { state: { searchHighlight: { id } } })
      }
   }

   viewAllResults() {
      const q = (this.searchQuery || '').trim()
      // Close the dropdown regardless so the click feels responsive.
      this.showSearchDropdown = false
      if (!q) {
         return
      }
      // Hand the already-fetched page-1 payload to the Search Results page
      // via NavigationExtras.state. The page consumes it on init and skips
      // the duplicate first API call. We only forward the cache when its
      // query actually matches the navigation query — guards against stale
      // state if the user typed-cleared-retyped quickly.
      const cacheQuery = (this.lastSearchData?.query || '').toString().trim().toLowerCase()
      const forwardCache = !!this.lastSearchData && cacheQuery === q.toLowerCase()
      this.router.navigate(['/leader-toolkit-search'], {
         queryParams: { q, page: 1, type: 'all' },
         state: forwardCache ? { initialSearchResults: this.lastSearchData } : undefined,
      })
   }
}

interface NavItem {
   label: string
   icon: string
   route: string
}
